import axios from 'axios';
import { getApiUrl } from '../config/environment';

const api = axios.create({
  baseURL: `${getApiUrl()}/facebook-ads`,
  timeout: 60000, // 60 segundos para sincronizações
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar token de autenticação
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interfaces
export interface FacebookConnection {
  id: string;
  account_id: number;
  account_name: string | null;
  token_type: string;
  permissions: string[];
  is_active: boolean;
  last_sync_at: string | null;
  error_message: string | null;
  created_at: string;
}

export interface FacebookCampaign {
  id: number;
  account_id: number;
  name: string;
  objective: string | null;
  status: string;
  created_time: string | null;
  updated_time: string | null;
  last_synced_at: string;
}

export interface FacebookAdSet {
  id: number;
  campaign_id: number;
  account_id: number;
  name: string;
  status: string;
  daily_budget: number | null;
  lifetime_budget: number | null;
  start_time: string | null;
  end_time: string | null;
  billing_event: string | null;
  optimization_goal: string | null;
  created_time: string | null;
  updated_time: string | null;
  last_synced_at: string;
}

export interface FacebookAd {
  id: number;
  adset_id: number;
  campaign_id: number;
  account_id: number;
  name: string;
  status: string;
  creative_id: number | null;
  created_time: string | null;
  updated_time: string | null;
  last_synced_at: string;
}

export interface CampaignPerformance {
  campaign_id: number;
  account_id: number;
  campaign_name: string;
  campaign_status: string;
  objective: string | null;
  campaign_created: string | null;
  total_spend: number;
  total_impressions: number;
  total_clicks: number;
  avg_cpc: number;
  avg_ctr: number;
  avg_cpm: number;
  total_reach: number;
  total_conversions: number;
  total_conversion_value: number;
  avg_cpa: number;
  roas: number;
  total_adsets: number;
  total_ads: number;
  first_date: string | null;
  last_date: string | null;
  days_with_data: number;
}

export interface FacebookStats {
  accountId: number;
  period: {
    startDate: string | null;
    endDate: string | null;
  };
  totals: {
    spend: number;
    impressions: number;
    clicks: number;
    conversions: number;
    conversionValue: number;
  };
  metrics: {
    avgCPC: number;
    avgCTR: number;
    avgCPM: number;
    avgCPA: number;
    roas: number;
  };
  counts: {
    campaigns: number;
    adsets: number;
    ads: number;
    leads: number;
  };
}

export interface LeadAttribution {
  contact_id: string;
  lead_name: string;
  email: string | null;
  phone: string | null;
  lead_created_at: string;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign_id: string | null;
  utm_adset_id: string | null;
  utm_ad_id: string | null;
  utm_term: string | null;
  campaign_id: number | null;
  campaign_name: string | null;
  campaign_status: string | null;
  campaign_objective: string | null;
  adset_id: number | null;
  adset_name: string | null;
  adset_budget: number | null;
  ad_id: number | null;
  ad_name: string | null;
  ad_status: string | null;
  ad_spend_on_lead_date: number | null;
  ad_impressions_on_lead_date: number | null;
  ad_clicks_on_lead_date: number | null;
  ad_cpc_on_lead_date: number | null;
  ad_ctr_on_lead_date: number | null;
}

export interface SyncResult {
  success: boolean;
  campaignsSynced: number;
  adsetsSynced: number;
  adsSynced: number;
  insightsSynced: number;
  errors: string[];
}

class FacebookAdsService {
  // ========== CONEXÕES ==========
  
  static async listConnections(): Promise<{ success: boolean; data?: FacebookConnection[]; error?: string }> {
    try {
      const response = await api.get('/connections');
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao listar conexões',
      };
    }
  }

  static async getConnection(accountId: string): Promise<{ success: boolean; data?: FacebookConnection; error?: string }> {
    try {
      const response = await api.get(`/connections/${accountId}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao buscar conexão',
      };
    }
  }

  static async createConnection(data: {
    accountId: string;
    accessToken: string;
    appId?: string;
    permissions?: string[];
  }): Promise<{ success: boolean; message?: string; data?: any; error?: string }> {
    try {
      const response = await api.post('/connections', data);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao criar conexão',
      };
    }
  }

  static async deleteConnection(accountId: string): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await api.delete(`/connections/${accountId}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao remover conexão',
      };
    }
  }

  // ========== SINCRONIZAÇÃO ==========

  static async syncFull(accountId: string): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await api.post(`/sync/${accountId}/full`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao iniciar sincronização',
      };
    }
  }

  static async syncIncremental(accountId: string): Promise<{ success: boolean; message?: string; data?: SyncResult; error?: string }> {
    try {
      const response = await api.post(`/sync/${accountId}/incremental`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao sincronizar',
      };
    }
  }

  // ========== CAMPANHAS ==========

  static async listCampaigns(
    accountId: string,
    params?: { limit?: number; offset?: number; status?: string }
  ): Promise<{ success: boolean; data?: FacebookCampaign[]; total?: number; error?: string }> {
    try {
      const response = await api.get(`/campaigns/${accountId}`, { params });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao listar campanhas',
      };
    }
  }

  static async getCampaignPerformance(
    accountId: string,
    params?: { startDate?: string; endDate?: string }
  ): Promise<{ success: boolean; data?: CampaignPerformance[]; error?: string }> {
    try {
      const response = await api.get(`/campaigns/${accountId}/performance`, { params });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao buscar performance',
      };
    }
  }

  static async getAdSets(campaignId: string): Promise<{ success: boolean; data?: FacebookAdSet[]; error?: string }> {
    try {
      const response = await api.get(`/campaign/${campaignId}/adsets`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao listar adsets',
      };
    }
  }

  static async getAds(adsetId: string): Promise<{ success: boolean; data?: FacebookAd[]; error?: string }> {
    try {
      const response = await api.get(`/adset/${adsetId}/ads`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao listar anúncios',
      };
    }
  }

  // ========== ESTATÍSTICAS ==========

  static async getStats(
    accountId: string,
    params?: { startDate?: string; endDate?: string }
  ): Promise<{ success: boolean; data?: FacebookStats; error?: string }> {
    try {
      const response = await api.get(`/stats/${accountId}`, { params });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao buscar estatísticas',
      };
    }
  }

  // ========== LEADS ATRIBUÍDOS ==========

  static async getLeads(
    accountId: string,
    params?: { limit?: number; offset?: number }
  ): Promise<{ success: boolean; data?: LeadAttribution[]; total?: number; error?: string }> {
    try {
      const response = await api.get(`/leads/${accountId}`, { params });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao listar leads',
      };
    }
  }

  // ========== Gerenciamento (ads_management) ==========

  static async pauseCampaign(campaignId: string): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await api.post(`/campaigns/${campaignId}/pause`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao pausar campanha',
      };
    }
  }

  static async activateCampaign(campaignId: string): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await api.post(`/campaigns/${campaignId}/activate`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao ativar campanha',
      };
    }
  }

  static async pauseAdSet(adsetId: string): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await api.post(`/adsets/${adsetId}/pause`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao pausar adset',
      };
    }
  }

  static async activateAdSet(adsetId: string): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await api.post(`/adsets/${adsetId}/activate`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao ativar adset',
      };
    }
  }

  static async pauseAd(adId: string): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await api.post(`/ads/${adId}/pause`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao pausar anúncio',
      };
    }
  }

  static async activateAd(adId: string): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await api.post(`/ads/${adId}/activate`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao ativar anúncio',
      };
    }
  }

  static async updateAdSetBudget(
    adsetId: string,
    dailyBudget: number
  ): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await api.put(`/adsets/${adsetId}/budget`, { dailyBudget });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao atualizar orçamento',
      };
    }
  }

  static async updateCampaignName(
    campaignId: string,
    name: string
  ): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await api.put(`/campaigns/${campaignId}/name`, { name });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao atualizar nome',
      };
    }
  }

  static async duplicateCampaign(
    campaignId: string,
    newName: string
  ): Promise<{ success: boolean; message?: string; data?: { originalCampaignId: number; newCampaignId: number }; error?: string }> {
    try {
      const response = await api.post(`/campaigns/${campaignId}/duplicate`, { newName });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao duplicar campanha',
      };
    }
  }

  static async deleteCampaign(campaignId: string): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await api.delete(`/campaigns/${campaignId}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao deletar campanha',
      };
    }
  }

  static async archiveCampaign(campaignId: string): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await api.post(`/campaigns/${campaignId}/archive`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao arquivar campanha',
      };
    }
  }

  static async deleteAdSet(adsetId: string): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await api.delete(`/adsets/${adsetId}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao deletar adset',
      };
    }
  }

  static async archiveAdSet(adsetId: string): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await api.post(`/adsets/${adsetId}/archive`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao arquivar adset',
      };
    }
  }

  static async updateAdSet(adsetId: string, updates: any): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await api.put(`/adsets/${adsetId}`, updates);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao atualizar adset',
      };
    }
  }

  static async duplicateAdSet(adsetId: string, newName: string): Promise<{ success: boolean; message?: string; data?: { originalAdSetId: number; newAdSetId: number }; error?: string }> {
    try {
      const response = await api.post(`/adsets/${adsetId}/duplicate`, { newName });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao duplicar adset',
      };
    }
  }

  static async deleteAd(adId: string): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await api.delete(`/ads/${adId}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao deletar anúncio',
      };
    }
  }

  static async archiveAd(adId: string): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await api.post(`/ads/${adId}/archive`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao arquivar anúncio',
      };
    }
  }

  static async updateAd(adId: string, updates: any): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await api.put(`/ads/${adId}`, updates);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao atualizar anúncio',
      };
    }
  }

  static async duplicateAd(adId: string, newName: string): Promise<{ success: boolean; message?: string; data?: { originalAdId: number; newAdId: number }; error?: string }> {
    try {
      const response = await api.post(`/ads/${adId}/duplicate`, { newName });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao duplicar anúncio',
      };
    }
  }

  static async createCampaign(campaignData: {
    accountId: string;
    name: string;
    objective: string;
    status?: 'ACTIVE' | 'PAUSED';
    special_ad_categories?: string[];
  }): Promise<{ success: boolean; message?: string; data?: { campaignId: number }; error?: string }> {
    try {
      const response = await api.post('/campaigns', campaignData);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao criar campanha',
      };
    }
  }

  static async createAd(adData: { adsetId: string; accountId: string; name: string; creative?: any; status?: string }): Promise<{ success: boolean; message?: string; data?: { adId: number }; error?: string }> {
    try {
      const response = await api.post('/ads', adData);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao criar anúncio',
      };
    }
  }

  static async bulkPauseCampaigns(campaignIds: string[], accountId: string): Promise<{ success: boolean; message?: string; data?: { success: number; failed: number; errors: string[] }; error?: string }> {
    try {
      const response = await api.post('/campaigns/bulk/pause', { campaignIds, accountId });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao pausar campanhas',
      };
    }
  }

  static async bulkActivateCampaigns(campaignIds: string[], accountId: string): Promise<{ success: boolean; message?: string; data?: { success: number; failed: number; errors: string[] }; error?: string }> {
    try {
      const response = await api.post('/campaigns/bulk/activate', { campaignIds, accountId });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao ativar campanhas',
      };
    }
  }

  static async bulkDeleteCampaigns(campaignIds: string[], accountId: string): Promise<{ success: boolean; message?: string; data?: { success: number; failed: number; errors: string[] }; error?: string }> {
    try {
      const response = await api.post('/campaigns/bulk/delete', { campaignIds, accountId });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao deletar campanhas',
      };
    }
  }
}

export default FacebookAdsService;

